import type { Asset, AssetClientContext, UploadPhase as ClientPhase } from "@pej/asset-client";
import { assetKeys, uploadAsset } from "@pej/asset-client";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import type { UploadPhase as FieldPhase } from "@/components/assets";
import { toFieldPhase, toUploadFile } from "./asset-projections";

/**
 * Drives one upload and reports it in the shape `AssetField` renders.
 *
 * Upload is the one asset operation that is not request/response: it hashes,
 * transfers, then waits on server-side verification, and the field shows all
 * three. The client emits those as phases; this hook holds the latest one and
 * projects it (see `toFieldPhase` for why the two unions differ).
 *
 * `ready` is the interesting phase, and the reason `onReady` exists rather than
 * a returned value: a finished upload is not a phase the field renders, it is
 * the field taking a value. The host writes that value into the form.
 *
 * `onFailed` is for an upload that stopped without a verdict — the service
 * refusing the reservation, or the network dropping. A cancel is not a failure
 * and does not call it.
 */
export function useAssetUpload(ctx: AssetClientContext | null, onReady: (asset: Asset) => void, onFailed: () => void) {
    const queryClient = useQueryClient();
    const [clientPhase, setClientPhase] = useState<ClientPhase | null>(null);
    const [totalBytes, setTotalBytes] = useState(0);
    const abortRef = useRef<AbortController | null>(null);
    /**
     * Upload outlives a fast navigation, and setting state on an unmounted
     * component is a warning at best and a leak at worst. The abort in cleanup
     * also stops the transfer itself, which is the honest behaviour: nothing is
     * left to receive the result.
     */
    const aliveRef = useRef(true);

    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
            abortRef.current?.abort();
        };
    }, []);

    const start = useCallback(
        async (files: FileList, folderId: string | null) => {
            // Single-image slot: the field holds one asset, so a multi-file drop or selection
            // takes the first and ignores the rest by design.
            const picked = files.item(0);
            if (!ctx || !picked) return;
            const file = toUploadFile(picked);

            abortRef.current?.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            setTotalBytes(file.size);

            try {
                const asset = await uploadAsset(ctx, {
                    file,
                    displayName: file.name,
                    folderId,
                    signal: controller.signal,
                    onPhase: (phase) => {
                        if (aliveRef.current) setClientPhase(phase);
                    },
                });
                if (!aliveRef.current) return;
                // `uploadAsset` resolves with the SETTLED asset — READY or
                // REJECTED — and throws only on transport failure. So a
                // resolved promise is not a success, and taking it as one would
                // put a rejected asset in a field that holds only ready ones
                // (integration design, decision 6).
                //
                // On rejection the client has already emitted a `rejected`
                // phase through `onPhase`; leave it on screen. Clearing it here
                // would erase the explanation before it could be read.
                await queryClient.invalidateQueries({ queryKey: assetKeys.all(ctx.workspaceId) });
                if (asset.state !== "READY") return;
                setClientPhase(null);
                onReady(asset);
            } catch {
                if (!aliveRef.current) return;
                if (controller.signal.aborted) {
                    setClientPhase(null);
                    return;
                }
                // Failed outright. A rejection the client already reported is
                // the more specific message, so it stays on screen. Anything
                // else was still in progress ("Uploading — 0%") and would sit
                // there forever: clear it and let the host say what happened.
                setClientPhase((last) => (last?.phase === "rejected" ? last : null));
                onFailed();
            }
        },
        [ctx, onReady, onFailed, queryClient],
    );

    const cancel = useCallback(() => {
        abortRef.current?.abort();
        setClientPhase(null);
    }, []);

    const phase: FieldPhase = toFieldPhase(clientPhase, totalBytes);
    return { phase, start, cancel };
}
